import { CheckedIcon } from 'assests/GmsIcons';
import React from 'react';

type TStep = {
    number: number;
    title: string;
    isActive: boolean;
    isCompleted: boolean;
}

type TStepperProps = {
    currentStep: number;
    onStepChange: (step: number) => void;
    steps: TStep[];
    payload: TRecategorizePayload;
    id?: string;
}

const Stepper = ({ currentStep, onStepChange, steps, payload, id = "product-recategorize-modal-stepper" }: TStepperProps) => {
    const handleStepClick = (stepNumber: number) => {
        if (stepNumber <= currentStep || stepNumber === currentStep + 1) {
            onStepChange(stepNumber);
        }
    };

    const isStepDisabled = (step: TStep) => {
        const isInvalidStep = (!step.isCompleted && step.number > currentStep + 1);
        const isPayloadInvalid = payload?.target_category === null;
        return isInvalidStep || isPayloadInvalid;
    };

    return (
        <div id={id} className="tw-w-full tw-max-w-xl tw-mx-auto">
            <div className="tw-flex tw-items-center tw-justify-between">
                {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                    <div id={`${id}-step-${index}`} className="tw-flex tw-flex-col tw-items-center">
                        <button
                            id={`${id}-step-${index}-btn`}
                            data-testid={`${id}-step-${index}-btn`}
                            onClick={() => handleStepClick(step.number)}
                                disabled={true}
                            className={`
                                tw-relative 
                                tw-flex tw-items-center tw-justify-center 
                                tw-w-8 tw-h-8 tw-rounded-full tw-font-medium tw-border-2 
                                tw-transform hover:tw-scale-110 
                                tw-transition-all tw-duration-300 tw-ease-in-out
                                ${
                                    /*
                                     * A COMPLETED step draws `CheckedIcon`, whose markup is
                                     * `<circle fill="currentColor">` plus `<path stroke="white">` —
                                     * a knockout glyph. On `ink-inverse` it painted a WHITE disc
                                     * carrying a WHITE tick, so in LIGHT mode the completed step was
                                     * an empty ring (reported 2026-09-13). It only looked right in
                                     * dark, where `--ds-ink-inverse` is #0b1220.
                                     *
                                     * The ink here is the DISC's colour, not the tick's: `accent`
                                     * matches the circle's own ground and lets the baked white tick
                                     * read in both themes. The ACTIVE step keeps `ink-inverse`,
                                     * because there the ink IS the glyph — a number on the fill.
                                     */
                                    step.isCompleted
                                        ? 'tw-border-accent tw-bg-accent tw-text-accent tw-cursor-pointer animate-bounce' 
                                        : step.isActive
                                        ? 'tw-border-accent tw-bg-accent tw-text-ink-inverse tw-cursor-pointer animate-fade-in' 
                                        : 'tw-border-edge-strong tw-text-ink-tertiary tw-text-sm'
                                }
                                ${step.number <= currentStep + 1 ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed'}
                            `}
                        >
                            <div 
                                className={`
                                    tw-absolute tw-inset-0 
                                    tw-flex tw-items-center tw-justify-center 
                                    tw-transition-all tw-duration-300 tw-ease-in-out
                                    ${step.isCompleted ? 'tw-opacity-0 tw-scale-75' : 'tw-opacity-100 tw-scale-100'}`}
                            >
                                {step.number}
                            </div>
                            <div 
                                className={`
                                    tw-absolute tw-inset-0 
                                    tw-flex tw-items-center tw-justify-center 
                                    tw-transition-all tw-duration-300 tw-ease-in-out
                                    ${step.isCompleted ? 'tw-opacity-100 tw-scale-100' : 'tw-opacity-0 tw-scale-75'}`}
                            >
                                <CheckedIcon />
                            </div>
                        </button>
                        <div
                            className={`
                                tw-mt-2 tw-text-sm
                                tw-transition-colors tw-duration-300 tw-ease-in-out
                                ${step.isCompleted || step.isActive ? 'tw-text-accent' : 'tw-text-ink-tertiary'}`}
                        >
                            {step.title}
                        </div>
                    </div>
                    {index < steps.length - 1 && (
                        <div className="tw-flex-1 tw-mx-4">
                            <div className="tw-relative tw-h-0.5">
                                <div className="tw-absolute tw-inset-0 tw-bg-edge" />
                                <div
                                    className={`
                                        tw-absolute tw-inset-0 
                                        tw-bg-accent
                                        tw-transition-transform tw-duration-500 tw-ease-in-out
                                        tw-origin-left
                                        ${step.isCompleted ? 'tw-scale-x-100' : 'tw-scale-x-0'}
                                    `}
                                />
                            </div>
                        </div>
                    )}
                </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default Stepper;